import { useCustomToast } from '@elkaso-app/web-design';
import http from 'apis/config/http-service';
import { useMutationWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import type { UseMutationOptions, UseMutationResult } from 'react-query';
import { useGetExclusiveDealsApi } from './use-get-exclusive-deals-api';

const endpoint = 'ums/v1/cms/exclusive-deals/sheet/upload';
export const uploadDealsSheetEndpointIdentifier = endpoint;

export interface UploadDealsSheetResultDto {
  created: number;
  updated: number;
}

export interface UploadDealsSheetErrorDto {
  statusCode?: number;
  message: string;
  // All-or-nothing: a 400 carries per-row reasons here ("Row N: <reason>"). Nothing is written.
  errors?: string[];
}

type TData = { data: UploadDealsSheetResultDto };
type TError = AxiosError<UploadDealsSheetErrorDto>;
type TVariables = { supplierId: number; body: FormData };
type Options = Omit<UseMutationOptions<TData, TError, TVariables, unknown>, 'mutationFn'>;
type ReturnType = UseMutationResult<TData, TError, TVariables>;

/**
 * Upload a filled exclusive-deals Excel sheet for a single supplier via
 * POST /ums/v1/cms/exclusive-deals/sheet/upload?supplierId=N (multipart, field `file`).
 * Rows with a Deal ID update; rows with deal data and no Deal ID create; catalog-only rows
 * are skipped. All-or-nothing: a 400 carries per-row messages in `errors` and nothing is written.
 */
export function useUploadDealsSheetApi(options?: Options): ReturnType {
  const { refetch: refetchDeals } = useGetExclusiveDealsApi({ enabled: false });
  const toast = useCustomToast();

  const mutationFn = async ({ supplierId, body }: TVariables): Promise<TData> => {
    try {
      const { data } = await http.post(endpoint, body, {
        params: { supplierId },
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return data;
    } catch (error: any) {
      if (!error.response?.status || !error.response?.data?.message) throw error;
      // Per-row errors are rendered in the modal; show the summary message as a toast.
      toast({ description: error.response.data.message, status: 'error' });
      throw error;
    }
  };

  return useMutationWrapper(mutationFn, {
    mutationKey: [endpoint],
    onSuccess: (data: TData) => {
      refetchDeals();
      const { created, updated } = data.data;
      toast({ description: `Uploaded: ${created} created, ${updated} updated`, status: 'success' });
    },
    ...options,
  });
}
